<template>
  <div ref="renderedGrid">
    <b-table striped borderless hover responsive :items="records.lines" :fields="records.fields"
             sort-by="datEvent" caption-top foot-clone no-footer-sorting
             v-if="records && records.lines && records.fields">
      <template slot="table-caption" class="pb-0" v-if="records.startDate && records.endDate">
        <div class="d-flex align-items-center justify-content-between table-caption-wrapper">
          <span class="table-caption-text">
            De {{records.startDate}} até {{records.endDate}}
          </span>
          <div class="no-print">
            <b-button variant="outline-dark" class="caption-btn" @click="exportData">
              <b-img fluid src="@/assets/export.svg"
                     class="d-none d-md-inline-block" alt="Exportar"></b-img>
              <b-img fluid src="@/assets/export-mobile.svg"
                     class="d-xs-inline-block d-md-none" alt="Exportar"></b-img>
              <span class="pl-1 d-none d-md-inline-block">Exportar</span>
            </b-button>
            <b-button ref="printBtn" variant="outline-dark"
                      class="caption-btn d-none d-md-inline-block" @click="print">
              <b-img fluid src="@/assets/printer.svg" alt="Imprimir"></b-img>
              <span class="pl-1">Imprimir</span>
          </b-button>
        </div>
        </div>
      </template>

      <template v-for="(field, index) in records.fields" :slot="field.key" slot-scope="row">
        <div :key="index" v-if="field.detailsField && row.value > 0">
            {{row.value}}
          <b-link v-if="row.item[field.detailsField]" class="detailsLink"
            href="#" @click.stop="showDetails(row, field.detailsField)">
            <span class="pl-2">Mais detalhes</span>
            <b-img class="pl-1" src="@/assets/plus.svg" fluid alt="Mais detalhes"></b-img>
          </b-link>
        </div>
        <div :key="index" v-else>
          {{row.value}}
        </div>
      </template>
      <!-- eslint-disable-next-line vue/no-unused-vars -->
      <template slot="row-details" slot-scope="row">
        <b-table bordered small :items="currentDetails.lines" :fields="currentDetails.fields">
        </b-table>
      </template>

      <template v-for="(record, index) in records.totals" :slot="'FOOT_' + record.column">
        <strong :key="index">{{formatTotalValue(record)}}</strong>
      </template>
    </b-table>
  </div>
</template>

<script>
import XLSX from 'xlsx';
import { getStringFromDate } from '@/utils/dateUtils';

export default {
  name: 'DataGrid',
  data() {
    return {
      currentDetails: {
        lines: [],
        fields: [],
      },
    };
  },
  props: {
    records: {
      type: Object,
      required: true,
      validator(obj) {
        return (obj
          && Object.prototype.hasOwnProperty.call(obj, 'lines') && obj.lines.length > 0
          && Object.prototype.hasOwnProperty.call(obj, 'totals') && obj.totals.length >= 0
          && Object.prototype.hasOwnProperty.call(obj, 'fields') && obj.fields.length > 0);
      },
    },
  },
  watch: {
    records: 'checkAllFields',
  },
  methods: {
    dateFormatter(value) {
      return getStringFromDate(new Date(value));
    },
    monetaryFormatter(value) {
      return `R$ ${value.toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.')}`;
    },
    formatTotalValue(total) {
      switch (total.column) {
        case 'numSupplierWinValue':
        case 'numSupplierWinCommission':
        case 'numSupplierWinBonus':
        case 'secondTierWinValue':
          return this.monetaryFormatter(total.value);

        default:
          return total.value;
      }
    },
    showDetails(row, detailsField) {
      const currRow = row;
      if (this.records && this.records.lines) {
        this.records.lines.forEach((l) => {
          const currLine = l;
          if (currRow.item !== currLine) {
            this.$set(currLine, '_showDetails', false);
          }
        });
      }
      this.$set(this, 'currentDetails', currRow.item[detailsField]);
      this.$set(currRow.item, '_showDetails', !currRow.item._showDetails);
    },
    applyDefaultClassesAndFormatters(f, { headerClass = 'generalColor', cellClass = 'reportCell' } = {}) {
      const currField = f;
      if (!currField.thClass) {
        this.$set(currField, 'thClass', `reportHeader position-relative ${headerClass}`);
      }
      if (!currField.tdClass) {
        this.$set(currField, 'tdClass', cellClass);
      }
      if (currField.type) {
        switch (currField.type) {
          case 'date':
            this.$set(currField, 'formatter', value => this.dateFormatter(value));
            break;

          case 'monetary':
            this.$set(currField, 'formatter', value => this.monetaryFormatter(value));
            break;

          default:
            break;
        }

        delete currField.type;
      }
    },
    applyDetailsClassesAndFormatters(f) {
      this.applyDefaultClassesAndFormatters(f, { headerClass: 'detailsColor' });
    },
    hasDetailsField(f) {
      return !!f.detailsField;
    },
    addShowDetailsField(f) {
      this.$set(f, '_showDetails', false);
    },
    checkAllFields() {
      if (this.records && this.records.fields) {
        this.records.fields.forEach(this.applyDefaultClassesAndFormatters);
        if (this.records.fields.some(this.hasDetailsField) && this.records.lines) {
          this.records.lines.forEach((f) => {
            Object.values(f).forEach((v) => {
              if (typeof v === 'object' && v.fields) {
                v.fields.forEach(this.applyDetailsClassesAndFormatters);
              }
            });
          });
          this.records.lines.forEach(this.addShowDetailsField);
        }
      }
    },
    print() {
      this.$emit('print');
      window.print();
    },
    getExportData() {
      const copy = JSON.parse(JSON.stringify(this.records, (k, v) => (typeof v === 'function' ? true : v)));
      const exportData = [];
      let currObj = {};

      currObj.sheetName = 'Geral';
      currObj.data = copy.lines;
      if (copy.totals && copy.totals.length) {
        let currentTotalObj;
        const totalsAsLine = copy.totals.reduce((obj, item) => {
          currentTotalObj = obj;
          currentTotalObj[item.column] = item.value;
          return obj;
        }, {});
        currObj.data.push(totalsAsLine);
      }
      currObj.data = this.normalizeData(copy.lines, copy.fields);
      currObj.header = this.normalizeHeader(copy.fields);
      exportData.push(currObj);

      const detailsPropertiesNames = copy.fields.filter(f => f.detailsField).map(f => f.detailsField).sort();
      detailsPropertiesNames.forEach((dn) => {
        currObj = {};
        const linesWithDetails = copy.lines.filter(l => l[dn]);
        const details = linesWithDetails.map(d => d[dn]);
        if (details && details.length > 0 && details[0] && details[0].fields) {
          currObj.sheetName = this.normalizeWorkSheetName(dn);
          currObj.data = this.normalizeData([].concat(...details.map(d => d.lines)),details[0].fields);
          currObj.header = this.normalizeHeader(details[0].fields);
          exportData.push(currObj);
        }
      });

      return exportData;
    },
    normalizeWorkSheetName(workSheetName) {
      return workSheetName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    },
    normalizeHeader(fields) {
      return fields.map(f => f.label);
    },
    normalizeData(lines, fields) {
      let relatedColumn;
      const normalizedLines = [];
      const monetaryKeys = ['numSupplierWinValue','secondTierWinValue','numSupplierWinBonus','numSupplierWinCommission'];
      let currentLineObject;
      lines.forEach((l) => {
        currentLineObject = {};
        Object.getOwnPropertyNames(l).forEach((lk) => {
          relatedColumn = fields.find(f => f.key === lk);
          if (relatedColumn) {
            if (relatedColumn.formatter && relatedColumn.key === 'datEvent') {
              const dateFormatted = this.dateFormatter(l[lk]);
              currentLineObject[relatedColumn.label] = /^\d{2}\/\d{2}\/\d{4}$/.test(dateFormatted) ? dateFormatted : l[lk];
            } else if (relatedColumn.formatter && monetaryKeys.includes(relatedColumn.key)) {
              currentLineObject[relatedColumn.label] = this.monetaryFormatter(l[lk]);
            } else {
              currentLineObject[relatedColumn.label] = l[lk];
            }
          }
        });
        normalizedLines.push(currentLineObject);
      });
      return normalizedLines;
    },
    exportData() {
      this.$emit('export');
      const exportData = this.getExportData();
      exportData[0].data.sort((a, b) => {
        const aa = a.Data.split('/');
        const bb = b.Data.split('/');
        return aa[2] - bb[2] || aa[1] - bb[1] || aa[0] - bb[0];
      });
      const wb = XLSX.utils.book_new();
      let ws;
      let colLengths;
      exportData.forEach((ed) => {
        ws = XLSX.utils.json_to_sheet(ed.data, { header: ed.header });

        colLengths = ed.header.map(h => ({ wch: h.length }));
        let lineLengths;
        ed.data.forEach((c) => {
          lineLengths = Object.getOwnPropertyNames(c).map(k => c[k].toString().length);
          for (let i = 0; i < colLengths.length; i += 1) {
            colLengths[i].wch = Math.max(colLengths[i].wch, lineLengths[i]);
          }
        });
        ws['!cols'] = colLengths;

        XLSX.utils.book_append_sheet(wb, ws, ed.sheetName);
      });

      XLSX.writeFile(wb, 'Relatório.xlsx', { compression: true });
    },
  },
  created() {
    this.checkAllFields();
  },
};
</script>

<style>
@media print {
  @page {
    size: A4 landscape;
    margin: 1cm;
  }

  body {
    background: white;
    color: black;
  }

  .no-print, button, .btn, .b-button {
    display: none !important;
  }

  table {
    width: 100% !important;
    table-layout: fixed;
    border-collapse: collapse;
  }

  th, td {
    white-space: normal !important;
    word-break: break-word;
    font-size: 12px;
    padding: 6px;
  }

  tr {
    page-break-inside: avoid;
  }

  .b-table + .b-table {
    page-break-before: always;
  }
}

</style>


<style lang="scss" scoped>
/deep/ {
  .table-caption-wrapper {
    padding: 5px 0;
  }

  .table-caption-text {
    padding-left: 12px;
  }

  caption {
    color: $text-dark;
    font-size: 18px;
    font-weight: bold;
    padding: 0;
    @include border-dark();
  }

  .caption-btn {
    border-color: transparent;

    &:focus {
      box-shadow: none;
    }

    &:active,
    &:hover {
      color: #343a40 !important;
      border-color: transparent !important;
      background-color: transparent !important;
    }
  }

  .reportHeader {
    font-size: 18px;
    font-weight: bold;
    padding: 10px 25px 10px 10px;
    vertical-align: middle !important;
    white-space: nowrap;

    &.generalColor {
      color: $text-light !important;
      background-color: $color-2 !important;
    }

    &.detailsColor {
      color: $text-dark !important;
      background-color: $alto !important;
    }
  }

  thead {
    tr {
      th[aria-sort][aria-sort]::before,
      th[aria-sort][aria-sort]::after {
        opacity: 0.4 !important;
      }

      th[aria-sort][aria-sort='descending']::before {
        opacity: 1 !important;
      }

      th[aria-sort][aria-sort='ascending']::after {
        opacity: 1 !important;
      }
    }

    .reportHeader {
      &::before {
        content: '' !important;
        position: absolute;
        right: 10px !important;
        padding: 0 !important;
        width: 0 !important;
        height: 0 !important;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        top: 14px !important;
        border-bottom: 8px solid $text-light;
      }

      &::after {
        content: '' !important;
        position: absolute;
        right: 10px !important;
        padding: 0 !important;
        width: 0 !important;
        height: 0 !important;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        bottom: 14px !important;
        border-top: 8px solid $text-light;
      }

      &.detailsColor {
        &::before {
          border-bottom-color: $dusty-gray;
        }

        &::after {
          border-top-color: $dusty-gray;
        }
      }
    }
  }

  .reportCell {
    padding: 10px 30px 10px 10px;
    border: none;
  }

  .table-striped tbody tr:nth-of-type(odd) > .reportCell {
    background-color: $bg-default !important;
  }

  .table-striped tbody tr:nth-of-type(even) > .reportCell {
    background-color: rgba(0, 0, 0, 0.05) !important;
  }

  .detailsLink {
    text-decoration: none;
  }
}
</style>
